import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Eye, EyeOff, Save, Key, Loader2, CheckCircle, AlertCircle,
  CreditCard, QrCode, Banknote, Upload, Image as ImageIcon, Zap
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PaymentSetting {
  setting_key: string;
  setting_value: string;
}

const PaymentSettings = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingQr, setIsUploadingQr] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);

  // Payment mode: 'manual', 'stripe', or 'beam'
  const [paymentMode, setPaymentMode] = useState<'manual' | 'stripe' | 'beam'>('manual');

  // Stripe settings
  const [stripePublishableKey, setStripePublishableKey] = useState("");

  // Manual payment settings
  const [manualQrCodeUrl, setManualQrCodeUrl] = useState("/kbank-bas.jpg");
  const [bankName, setBankName] = useState("KBANK กสิกรไทย");
  const [bankAccountNumber, setBankAccountNumber] = useState("457-2-10138-5");
  const [bankAccountName, setBankAccountName] = useState("รณภพ ชนะพิมพ์");

  // Beam settings
  const [beamMerchantId, setBeamMerchantId] = useState("");
  const [beamApiUrl, setBeamApiUrl] = useState("https://api.beamcheckout.com");
  const [showBeamKey, setShowBeamKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("payment_settings" as any)
        .select("setting_key, setting_value");

      if (error) {
        console.error("Error loading payment settings:", error);
        // Table might not exist yet, use defaults
        return;
      }

      const settings = (data as unknown as PaymentSetting[]) || [];
      const getValue = (key: string): string => {
        const setting = settings.find(s => s.setting_key === key);
        if (!setting) return "";
        // Parse JSON value
        try {
          return JSON.parse(setting.setting_value as string);
        } catch {
          return setting.setting_value as string;
        }
      };

      setPaymentMode((getValue("payment_mode") as 'manual' | 'stripe' | 'beam') || 'manual');
      setStripePublishableKey(getValue("stripe_publishable_key") || "");
      setManualQrCodeUrl(getValue("manual_qr_code_url") || "/kbank-bas.jpg");
      setBankName(getValue("bank_name") || "KBANK กสิกรไทย");
      setBankAccountNumber(getValue("bank_account_number") || "457-2-10138-5");
      setBankAccountName(getValue("bank_account_name") || "รณภพ ชนะพิมพ์");
      setBeamMerchantId(getValue("beam_merchant_id") || "");
      setBeamApiUrl(getValue("beam_api_url") || "https://api.beamcheckout.com");
    } catch (err) {
      console.error("Error loading payment settings:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Validate Stripe key if in stripe mode
      if (paymentMode === 'stripe' && !stripePublishableKey) {
        toast({
          title: "Validation Error",
          description: "Stripe Publishable Key is required when using Stripe payment mode.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      // Validate Beam keys if in beam mode
      if (paymentMode === 'beam' && !beamMerchantId) {
        toast({
          title: "Validation Error",
          description: "Beam Merchant ID is required when using BeamCheckout payment mode.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      const settings = [
        { setting_key: "payment_mode", setting_value: JSON.stringify(paymentMode) },
        { setting_key: "stripe_publishable_key", setting_value: JSON.stringify(stripePublishableKey) },
        { setting_key: "manual_qr_code_url", setting_value: JSON.stringify(manualQrCodeUrl) },
        { setting_key: "bank_name", setting_value: JSON.stringify(bankName) },
        { setting_key: "bank_account_number", setting_value: JSON.stringify(bankAccountNumber) },
        { setting_key: "bank_account_name", setting_value: JSON.stringify(bankAccountName) },
        { setting_key: "beam_merchant_id", setting_value: JSON.stringify(beamMerchantId) },
        { setting_key: "beam_api_url", setting_value: JSON.stringify(beamApiUrl) },
      ];

      let allSuccess = true;
      for (const setting of settings) {
        const { error } = await supabase
          .from("payment_settings" as any)
          .upsert(setting, { onConflict: "setting_key" });

        if (error) {
          console.error(`Error saving ${setting.setting_key}:`, error);
          allSuccess = false;
        }
      }

      if (allSuccess) {
        toast({
          title: "Settings Saved",
          description: "Payment settings have been saved successfully.",
        });
      } else {
        throw new Error("Failed to save some settings");
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      toast({
        title: "Save Failed",
        description: "Failed to save payment settings. Check admin permissions.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleQrUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid File",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 2MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploadingQr(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `qr-code-${Date.now()}.${fileExt}`;
      const filePath = `payment/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('payment-slips')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('payment-slips')
        .getPublicUrl(filePath);

      setManualQrCodeUrl(publicUrl);
      toast({
        title: "QR Code Uploaded",
        description: "QR code image has been uploaded successfully.",
      });
    } catch (err) {
      console.error("Error uploading QR code:", err);
      toast({
        title: "Upload Failed",
        description: "Failed to upload QR code image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploadingQr(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Payment Mode Toggle */}
      <Card variant="glass">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle>Payment Mode</CardTitle>
              <CardDescription>Choose how customers pay for credits</CardDescription>
            </div>
            <Badge variant={paymentMode === 'stripe' ? 'default' : paymentMode === 'beam' ? 'default' : 'secondary'}>
              {paymentMode === 'stripe' ? 'Stripe Auto' : paymentMode === 'beam' ? 'Beam 0%' : 'Manual'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Manual QR Option */}
            <div
              onClick={() => setPaymentMode('manual')}
              className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${paymentMode === 'manual'
                  ? 'border-green-500 bg-green-500/10 shadow-lg shadow-green-500/10'
                  : 'border-muted hover:border-muted-foreground/30 bg-muted/30'
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <QrCode className={`h-5 w-5 ${paymentMode === 'manual' ? 'text-green-500' : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">Manual QR</span>
              </div>
              <p className="text-xs text-muted-foreground">Customer scans QR, uploads slip, admin verifies</p>
            </div>

            {/* Stripe Option */}
            <div
              onClick={() => setPaymentMode('stripe')}
              className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${paymentMode === 'stripe'
                  ? 'border-purple-500 bg-purple-500/10 shadow-lg shadow-purple-500/10'
                  : 'border-muted hover:border-muted-foreground/30 bg-muted/30'
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <CreditCard className={`h-5 w-5 ${paymentMode === 'stripe' ? 'text-purple-500' : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">Stripe PromptPay</span>
              </div>
              <p className="text-xs text-muted-foreground">Auto credit + receipt via Stripe (~3% fee)</p>
            </div>

            {/* Beam Option */}
            <div
              onClick={() => setPaymentMode('beam')}
              className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${paymentMode === 'beam'
                  ? 'border-cyan-500 bg-cyan-500/10 shadow-lg shadow-cyan-500/10'
                  : 'border-muted hover:border-muted-foreground/30 bg-muted/30'
                }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <Zap className={`h-5 w-5 ${paymentMode === 'beam' ? 'text-cyan-500' : 'text-muted-foreground'}`} />
                <span className="font-semibold text-sm">BeamCheckout</span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-cyan-500/50 text-cyan-500">0% fee</Badge>
              </div>
              <p className="text-xs text-muted-foreground">PromptPay QR auto-verify, 0% transaction fee</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stripe Settings - Show when Stripe mode */}
      {paymentMode === 'stripe' && (
        <Card variant="glass">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <CardTitle>Stripe Configuration</CardTitle>
                <CardDescription>Configure Stripe for PromptPay payments</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="stripe-publishable-key">Publishable Key</Label>
              <div className="relative">
                <Input
                  id="stripe-publishable-key"
                  type={showStripeKey ? "text" : "password"}
                  value={stripePublishableKey}
                  onChange={(e) => setStripePublishableKey(e.target.value)}
                  placeholder="pk_test_..."
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowStripeKey(!showStripeKey)}
                >
                  {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {stripePublishableKey && (
                <div className="flex items-center gap-2 text-sm text-green-500">
                  <CheckCircle className="h-4 w-4" />
                  <span>Publishable key configured</span>
                </div>
              )}
              {!stripePublishableKey && (
                <div className="flex items-center gap-2 text-sm text-yellow-500">
                  <AlertCircle className="h-4 w-4" />
                  <span>Publishable key required</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Key className="h-4 w-4" />
              <span>
                Get your keys from{" "}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Stripe Dashboard
                </a>
              </span>
            </div>
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                <strong>Important:</strong> The Secret Key (sk_xxx) must be set as an environment variable in Supabase Edge Functions, not stored here.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BeamCheckout Settings - Show when Beam mode */}
      {paymentMode === 'beam' && (
        <Card variant="glass">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                <Zap className="h-5 w-5 text-cyan-500" />
              </div>
              <div>
                <CardTitle>BeamCheckout Configuration</CardTitle>
                <CardDescription>Configure BeamCheckout for PromptPay (0% fee)</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="beam-merchant-id">Merchant ID</Label>
              <div className="relative">
                <Input
                  id="beam-merchant-id"
                  type={showBeamKey ? "text" : "password"}
                  value={beamMerchantId}
                  onChange={(e) => setBeamMerchantId(e.target.value)}
                  placeholder="your-merchant-id"
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowBeamKey(!showBeamKey)}
                >
                  {showBeamKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {beamMerchantId && (
                <div className="flex items-center gap-2 text-sm text-green-500">
                  <CheckCircle className="h-4 w-4" />
                  <span>Merchant ID configured</span>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="beam-api-url">API URL</Label>
              <Input
                id="beam-api-url"
                value={beamApiUrl}
                onChange={(e) => setBeamApiUrl(e.target.value)}
                placeholder="https://api.beamcheckout.com"
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Use <code className="text-primary">https://playground.api.beamcheckout.com</code> for testing
              </p>
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Key className="h-4 w-4" />
              <span>
                Get your keys from{" "}
                <a href="https://merchant.beamcheckout.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Beam Merchant Portal
                </a>
              </span>
            </div>

            <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <p className="text-sm text-cyan-600 dark:text-cyan-400">
                <strong>Important:</strong> The API Key (BEAM_API_KEY) and Webhook Secret (BEAM_WEBHOOK_SECRET) must be set as environment variables in Supabase Edge Functions.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <p className="text-sm text-green-600 dark:text-green-400">
                ✅ <strong>0% Transaction Fee</strong> - BeamCheckout PromptPay ไม่มีค่าธรรมเนียม เครดิตจะเติมอัตโนมัติเมื่อชำระสำเร็จ
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Payment Settings - Show when Manual mode */}
      {paymentMode === 'manual' && (
        <Card variant="glass">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Banknote className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <CardTitle>Bank Transfer Settings</CardTitle>
                <CardDescription>Configure QR code and bank account details</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* QR Code Upload */}
            <div className="space-y-3">
              <Label>Payment QR Code</Label>
              <div className="flex items-start gap-4">
                <div className="w-32 h-32 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center overflow-hidden bg-muted/50">
                  {manualQrCodeUrl ? (
                    <img
                      src={manualQrCodeUrl}
                      alt="Payment QR Code"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleQrUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingQr}
                    className="gap-2"
                  >
                    {isUploadingQr ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {isUploadingQr ? "Uploading..." : "Upload QR Code"}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Recommended: 300x300px, PNG or JPG, max 2MB
                  </p>
                  <div className="space-y-1">
                    <Label htmlFor="qr-url" className="text-xs">Or enter URL directly:</Label>
                    <Input
                      id="qr-url"
                      value={manualQrCodeUrl}
                      onChange={(e) => setManualQrCodeUrl(e.target.value)}
                      placeholder="/kbank-bas.jpg"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bank-name">Bank Name</Label>
                <Input
                  id="bank-name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder="KBANK กสิกรไทย"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="account-number">Account Number</Label>
                <Input
                  id="account-number"
                  value={bankAccountNumber}
                  onChange={(e) => setBankAccountNumber(e.target.value)}
                  placeholder="457-2-10138-5"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="account-name">Account Name</Label>
              <Input
                id="account-name"
                value={bankAccountName}
                onChange={(e) => setBankAccountName(e.target.value)}
                placeholder="รณภพ ชนะพิมพ์"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving} className="gap-2">
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isSaving ? "Saving..." : "Save Payment Settings"}
        </Button>
      </div>
    </div>
  );
};

export default PaymentSettings;
